import { addHours, isAfter, subHours } from 'date-fns';

import { DateRange } from '@/store';
import { startOfHourUTC } from '@/utils/dateHelpers';

/**
 * The one number governing live data, doing two jobs on either side of the current hour.
 *
 * Backwards it is the late arrival tolerance: an event that ended before the boundary but arrived after it is still within the live window.
 * Forwards it is the overshoot: a range that ends up slightly into the future to keep the query key stable until
 * the explicit invalidation by refetchInterval, wavelink or the next hour rollover.
 */
export const LIVE_WINDOW_HOURS = 1;

export type SplitDateRange = {
    /** The settled part of the window. Its bounds do not move between hours, so it is fetched once. */
    settledDateRange?: DateRange;
    /**
     * The live edge, refetched in place. Absent when the range is entirely settled.
     *
     * Never longer than twice `LIVE_WINDOW_HOURS`, because this is the payload that gets pulled
     * repeatedly.
     */
    liveDateRange?: DateRange;
};

/**
 * Splits a range so the settled part gets a cache key that stays stable as the present advances, and
 * only the live edge is refetched.
 *
 * Every bound derived from the present sits on the UTC hour, and so does the start, whatever the caller
 * asked for. A bound that tracks the present must not reach the query key: `DateRangeContext` advances
 * `endDate` every 5 minutes for a live range, and `RecentDateTimeRangeContextProvider` derives `startDate`
 * from that same present, so either bound can move twelve times an hour. Snapping is what holds both keys
 * still between hour rollovers. An end that is already in the past is handed back as it came, since a
 * settled bound is not going anywhere for the key to chase.
 *
 * The union of the halves therefore *contains* `range` and can reach up to an hour earlier than asked
 * for, and never later than the live overshoot below. The caller owns the narrowing: `useLiveSplitQuery` clips the leading items back off the series it
 * hands out. Widening the start by an hour never widens a chunk past its grid cell, because every settled
 * width divides the day into whole hours, so a request still fits under the endpoint's result cap.
 *
 * The live half deliberately runs past the present to the next hour boundary. There is no data in the
 * future, so the overshoot costs nothing and is what keeps its key stable for the whole hour. For the
 * same reason a range reaching beyond the present (the hour-widened sound pressure window) still stops
 * at the next hour boundary: everything ahead of the present is empty regardless.
 *
 * `asOf` is only ever read down to its hour, so every instant within one UTC hour produces an identical
 * split. Callers rely on that: `useLiveSplitQuery` passes the hour itself so its memo can hold the
 * result for the hour rather than recomputing on every render.
 */
export const splitLiveRange = (range: DateRange, asOf: Date = new Date()): SplitDateRange => {
    /**
     * Rounded on the UTC clock face, never the local one, so the boundaries are the same instants for
     * every viewer. Only calendar-field helpers need that care: `addHours`, `subHours` and `isAfter`
     * are used bare here because durations and instant comparisons carry no timezone.
     */
    const currentHour = startOfHourUTC(asOf);
    const boundary = subHours(currentHour, LIVE_WINDOW_HOURS);
    const start = startOfHourUTC(range.startDate);

    // Settled in full: this window ended longer ago than the late arrival tolerance.
    const endsAtOrBeforeBoundary = !isAfter(range.endDate, boundary);
    if (endsAtOrBeforeBoundary) {
        return { settledDateRange: { startDate: start, endDate: range.endDate } };
    }

    const liveEnd = isAfter(range.endDate, currentHour)
        ? addHours(currentHour, LIVE_WINDOW_HOURS) // Overshoot to the next hour
        : range.endDate; // Requested endDate is between the boundary and the current hour. So it's real in-the-past end time that is just a bit within our late arrival tolerance.

    // Starts inside the live window too, so the whole range is live edge and there is no settled part to separate out.
    const startsAtOrAfterBoundary = !isAfter(boundary, start);
    if (startsAtOrAfterBoundary) {
        return { liveDateRange: { startDate: start, endDate: liveEnd } };
    }

    return {
        settledDateRange: { startDate: start, endDate: boundary },
        liveDateRange: { startDate: boundary, endDate: liveEnd },
    };
};

/**
 * Whether a range can still gain data, and so is worth polling and invalidating.
 *
 * Defined in terms of the split rather than independently, so "is this live" and "does this get a
 * refreshed live half" is the same thing.
 */
export const isLiveDateRange = (range?: DateRange | null, asOf: Date = new Date()): boolean => {
    if (!range) {
        /*
         * Defensive: every caller disables its query while the range is unresolved, so this answer is
         * not observable today. `true` is the safe direction if that changes, because answering
         * "settled" would mean `staleTime: Infinity` on a window we have not identified.
         */
        return true;
    }
    return splitLiveRange(range, asOf).liveDateRange !== undefined;
};

const MS_IN_HOUR = 1000 * 60 * 60;

/** `LIVE_WINDOW_HOURS` as a duration, for callers comparing it against timestamps. */
export const LIVE_WINDOW_MS = LIVE_WINDOW_HOURS * MS_IN_HOUR;

/**
 * A settled chunk width, in hours. Every member divides 24, which is what lands the grid it tiles on the
 * UTC clock face and puts a chunk's boundaries on the hours the live split cuts on.
 */
export type ChunkHours = 1 | 2 | 3 | 4 | 6 | 8 | 12 | 24;

/**
 * The widths the settled window is cut into, narrowest first, so no single request has to carry a whole
 * multi-day range.
 *
 * Each width divides the next, so the coarser grids are nested inside the finest one and every boundary
 * falls on a six hour line. All of them divide the day, which is what puts the grids on the UTC clock face.
 *
 * Six hours is the narrowest because it matches the window `getHistoricalWaveSpectrumData` already pages
 * `/api/spotter-wave-data` in: an endpoint that caps its result set needs a window small enough to fit
 * under the cap. A day is the widest a request can cover and still answer within the endpoint's timeout.
 */
export const SETTLED_CHUNK_HOURS: ChunkHours[] = [6, 12, 24];

/**
 * How many chunks a range is cut into before the next width up is used.
 *
 * Every chunk is a react-query entry that each chart section holds an observer on, and a tab full of
 * sections observes them all against the same range. The count is what that multiplies.
 */
export const MAX_SETTLED_CHUNKS = 16;

/**
 * The narrowest width that cuts `range` into no more than `MAX_SETTLED_CHUNKS` pieces, or the widest
 * available once no width can, and never wider than `maxHours`.
 *
 * `maxHours` is a ceiling rather than a choice of width, so a series dense enough to name one still gets
 * the narrower width a wide range would have picked for it.
 */
export const settledChunkHours = (range: DateRange, maxHours: ChunkHours = 24): ChunkHours => {
    const rangeMs = range.endDate.getTime() - range.startDate.getTime();
    const widest = SETTLED_CHUNK_HOURS[SETTLED_CHUNK_HOURS.length - 1];
    const forSpan = SETTLED_CHUNK_HOURS.find((hours) => rangeMs <= hours * MS_IN_HOUR * MAX_SETTLED_CHUNKS) ?? widest;

    return forSpan <= maxHours ? forSpan : maxHours;
};

/**
 * How many times a settled chunk is asked for again after a failure.
 *
 * A chunk that fails takes its whole section down with it, since the series is withheld until every chunk
 * is in hand. One attempt back rides out a transient failure without turning a wide range into a burst of
 * retries.
 */
export const SETTLED_CHUNK_RETRY = 1;

/**
 * Cuts a range into chunks aligned to the absolute grid of `chunkHours` since the epoch, clipped to the
 * range's own bounds at either end. The union of the chunks is exactly `range`, so the caller is asking
 * for the same data however the cuts fall.
 *
 * Alignment is what makes a chunk reusable: the window covering a given six hours has the same bounds,
 * and so the same query key, whatever wider range it was requested as part of.
 *
 * The grid lands on the UTC clock face because the epoch is a UTC midnight and every `ChunkHours` divides
 * 24, which is what keeps a boundary on the hour the live split cuts on.
 *
 * The width comes from the range's own span, so the chunk count stays under `MAX_SETTLED_CHUNKS` until
 * the widest width is reached. Crossing from one width to the next re-keys every chunk.
 */
export const chunkDateRange = (range: DateRange, chunkHours: ChunkHours = settledChunkHours(range)): DateRange[] => {
    const chunkMs = chunkHours * MS_IN_HOUR;
    const endMs = range.endDate.getTime();

    const chunks: DateRange[] = [];
    let startMs = range.startDate.getTime();

    while (startMs < endMs) {
        // strictly greater than `startMs`, so a start already sitting on the grid gets a whole chunk
        const nextGridLineMs = (Math.floor(startMs / chunkMs) + 1) * chunkMs;
        const chunkEndMs = Math.min(nextGridLineMs, endMs);
        chunks.push({ startDate: new Date(startMs), endDate: new Date(chunkEndMs) });
        startMs = chunkEndMs;
    }

    return chunks;
};
